import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AlertCircle, ChevronDown, ChevronUp, CheckCircle2 } from 'lucide-react';
import API_URL from '@/config/api';
import { Card } from '@/components/ui/card';

const gbp = (n) => `£${Number(n || 0).toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export function SmallBalancesCard({ max = 25 }) {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    fetch(`${API_URL}/api/staff/training-courses/small-balances?max_amount=${max}`, { credentials: 'include' })
      .then((r) => (r.ok ? r.json() : null)).then(setData).catch(() => {});
  }, [max]);

  if (!data) return null;
  const ok = data.count === 0;

  return (
    <Card className={`mb-4 ${ok ? 'border-emerald-200 bg-emerald-50/40' : 'border-amber-300 bg-amber-50/60'}`} data-testid="small-balances-card">
      <button type="button" onClick={() => !ok && setOpen((o) => !o)} className="w-full flex items-center gap-3 px-4 py-3 text-left" data-testid="small-balances-toggle">
        {ok ? <CheckCircle2 className="w-5 h-5 text-emerald-600 shrink-0" /> : <AlertCircle className="w-5 h-5 text-amber-600 shrink-0" />}
        <div className="flex-1 min-w-0">
          <div className="text-sm font-semibold text-gray-900">
            {ok ? `No small remainders — every balance is either £0 or over ${gbp(max)}` : `${data.count} trainee${data.count === 1 ? '' : 's'} with a small remainder (under ${gbp(max)})`}
          </div>
          <div className="text-xs text-gray-600">
            {ok ? 'Payment ledgers look clean.' : 'Usually an un-itemised payment note — open the course and correct the payment record so the ledger is accurate.'}
          </div>
        </div>
        {!ok && (open ? <ChevronUp className="w-4 h-4 text-gray-500" /> : <ChevronDown className="w-4 h-4 text-gray-500" />)}
      </button>
      {open && !ok && (
        <div className="border-t border-amber-200 divide-y divide-amber-100" data-testid="small-balances-list">
          {data.items.map((it) => (
            <button
              key={it.registration_id}
              type="button"
              onClick={() => navigate(`/staff/training-courses/${it.course_id}`)}
              className="w-full flex items-center justify-between gap-3 px-4 py-2 text-left hover:bg-amber-100/60"
              data-testid={`small-balance-row-${it.registration_id}`}
            >
              <div className="min-w-0">
                <div className="text-sm font-medium text-gray-900 truncate">{it.name}</div>
                <div className="text-xs text-gray-500 truncate">{it.course_name}{it.course_date ? ` · ${it.course_date}` : ''}</div>
              </div>
              <div className="text-right shrink-0">
                <div className="text-sm font-bold text-amber-700">{gbp(it.owed)}</div>
                {it.payment_total != null && <div className="text-[10px] text-gray-500">fee {gbp(it.payment_total)} · paid {gbp(it.payment_received)}</div>}
              </div>
            </button>
          ))}
        </div>
      )}
    </Card>
  );
}
